/**
 * Identity Token Provider
 * 
 * This module provides a bridge between the @workspace/identity-react authentication
 * and the existing feedbackApi token requirements.
 * 
 * When running in standalone mode (outside Evo tabs), this provider uses the
 * useAppIdentity hook to obtain OIDC access tokens from Access Identity.
 * 
 * It also provides a fallback to use saved OIDC state when the identity provider
 * hasn't finished initializing yet.
 */

import { loadOIDCState } from '../utils/oidcStateManager';

/**
 * Token provider function type
 */
export type TokenProvider = () => Promise<string | null>;

/**
 * Global token provider reference
 * This is set by the IdentityTokenBridge component when the app initialises
 */
let globalTokenProvider: TokenProvider | null = null;

/**
 * Set the global token provider
 * Called by IdentityTokenBridge when identity is available
 */
export const setTokenProvider = (provider: TokenProvider): void => {
  globalTokenProvider = provider;
};

/**
 * Clear the global token provider
 * Called when the user logs out or identity becomes unavailable
 */
export const clearTokenProvider = (): void => {
  globalTokenProvider = null;
};

/**
 * Get the current access token from the identity provider
 * 
 * If the identity provider is available, uses it to get the token.
 * Otherwise, falls back to checking for saved OIDC state.
 * 
 * @returns Promise resolving to the access token, or null if not available
 */
export const getIdentityToken = async (): Promise<string | null> => {
  // First try the identity provider if available
  if (globalTokenProvider) {
    try {
      const token = await globalTokenProvider();
      if (token) {
        return token;
      }
    } catch (error) {
      console.error('Error getting identity token from provider:', error);
    }
  }
  
  // Fall back to saved OIDC state if identity provider isn't ready
  const savedState = loadOIDCState();
  
  if (savedState?.isAuthenticated && savedState.accessToken) {
    return savedState.accessToken;
  }
  
  return null;
};

/**
 * Check if the identity token provider is available or if saved OIDC state has a token
 */
export const hasTokenProvider = (): boolean => {
  // Check if identity provider is available
  if (globalTokenProvider !== null) {
    return true;
  }
  
  // Check if saved OIDC state has a token
  const savedState = loadOIDCState();
  return !!(savedState?.isAuthenticated && savedState.accessToken);
};
